/**
 * /debug-sentry — Sentry integration test page.
 *
 * Throws a JavaScript error on button click.
 * Confirm the event appears in your Sentry dashboard within ~30 seconds.
 *
 * ⚠️  Remove this route before going to production, or protect it behind
 *     a system_admin guard so it's never accessible to end-users.
 */

import { useState } from "react";
import { Bug, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sentry } from "@/lib/sentry";
import { Link } from "react-router-dom";

export default function DebugSentry() {
  const [lastThrown, setLastThrown] = useState<string | null>(null);
  const [capturedManually, setCapturedManually] = useState<string | null>(null);

  // Throws an uncaught error — triggers Sentry's global error handler
  const throwUnhandledError = () => {
    const msg = `Sentry test error — ${new Date().toISOString()}`;
    setLastThrown(msg);
    throw new Error(msg);
  };

  // Manually captured error — useful for try/catch flows
  const captureManualError = () => {
    const error = new Error(`Manual Sentry capture — ${new Date().toISOString()}`);
    Sentry.captureException(error, {
      tags: { test: "true", source: "debug-sentry-page" },
      level: "error",
    });
    setCapturedManually(error.message);
  };

  // Sends a custom message event (non-exception)
  const captureMessage = () => {
    Sentry.captureMessage("Debug page message event", {
      level: "info",
      tags: { test: "true" },
    });
    setCapturedManually(`Message sent at ${new Date().toLocaleTimeString()}`);
  };

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const isProd = import.meta.env.PROD;

  return (
    <div className="container mx-auto max-w-2xl py-10 px-4 space-y-6">
      {/* Back link */}
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
          <Bug className="h-5 w-5 text-destructive" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight">Sentry Debug Panel</h1>
          <p className="text-sm text-muted-foreground">Test error reporting — events appear in dashboard within ~30 s</p>
        </div>
      </div>

      {/* Status card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Configuration Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">VITE_SENTRY_DSN</span>
            {dsn ? (
              <Badge variant="default" className="bg-green-600 text-white">Configured</Badge>
            ) : (
              <Badge variant="destructive">Not set</Badge>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Sentry enabled</span>
            <Badge variant={isProd ? "default" : "secondary"}>
              {isProd ? "Yes (production)" : "No (dev mode — events not sent)"}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Traces sample rate</span>
            <span className="font-medium">{isProd ? "10 %" : "100 %"}</span>
          </div>
          {dsn && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">DSN</span>
              <span className="font-mono text-xs text-muted-foreground truncate max-w-[260px]">{dsn.split("@")[1]}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {!isProd && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-800 dark:text-amber-400">Development mode — events not sent</p>
              <p className="text-amber-700 dark:text-amber-500 mt-0.5">
                Sentry is initialized but <code className="font-mono">enabled: false</code> in non-production builds.
                To test locally, temporarily set <code className="font-mono">enabled: true</code> in{" "}
                <code className="font-mono">src/lib/sentry.ts</code>, then rebuild.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test buttons */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Trigger Test Events</CardTitle>
          <CardDescription className="text-xs">
            Each button sends a different type of event to Sentry.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Unhandled error */}
          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Unhandled Error</p>
              <p className="text-xs text-muted-foreground">Throws directly — caught by Sentry's global handler</p>
            </div>
            <Button variant="destructive" size="sm" onClick={throwUnhandledError}>
              Throw Error
            </Button>
          </div>

          {/* Manual capture */}
          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Manual captureException</p>
              <p className="text-xs text-muted-foreground">Uses <code className="font-mono text-[11px]">Sentry.captureException()</code></p>
            </div>
            <Button variant="outline" size="sm" onClick={captureManualError}>
              Capture
            </Button>
          </div>

          {/* Message event */}
          <div className="flex items-center justify-between gap-4 rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">captureMessage</p>
              <p className="text-xs text-muted-foreground">Sends an info-level message event</p>
            </div>
            <Button variant="outline" size="sm" onClick={captureMessage}>
              Send Message
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {(lastThrown || capturedManually) && (
        <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20">
          <CardContent className="flex items-start gap-3 py-4">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm space-y-1">
              <p className="font-semibold text-green-800 dark:text-green-400">Event dispatched</p>
              {lastThrown && <p className="text-green-700 dark:text-green-500 font-mono text-xs">{lastThrown}</p>}
              {capturedManually && <p className="text-green-700 dark:text-green-500 font-mono text-xs">{capturedManually}</p>}
              <p className="text-green-600 dark:text-green-600 mt-1">
                Check your{" "}
                <a
                  href="https://keystone-33.sentry.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Sentry dashboard
                </a>{" "}
                — it should appear within 30 seconds.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
